import { FC, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  IdeaUpdateRequest,
  useDeleteIdeaMutation,
  useGetIdeaQuery,
  useGetIdeaVotesQuery,
  useGetOrganizationsSelectQuery,
  useGetUsersQuery,
  useUpdateIdeaMutation,
} from '../api/api';
import { Modal } from '../components/Modal';
import { IdeaEditorForm } from './ideas/shared';
import {
  ACCESSIBILITY_LABELS,
  CATEGORY_LABELS,
  STATUS_COLORS,
  STATUS_LABELS,
  VOTES_PAGE_SIZE,
  formatCriticality,
  formatDateTime,
  getErrorMessage,
} from './ideas/shared';

const formatPersonName = (firstName: string, lastName: string, email: string) => {
  const fullName = `${firstName} ${lastName}`.trim();
  return fullName || email;
};

const formatVoteCriticality = (importance: number | null | undefined) => {
  if (importance === 3) {
    return 'Критично';
  }
  if (importance === 2) {
    return 'Средне';
  }
  if (importance === 1) {
    return 'Низко';
  }
  return '—';
};

export const IdeaDetails: FC = () => {
  const navigate = useNavigate();
  const { ideaId } = useParams<{ ideaId: string }>();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editorError, setEditorError] = useState<string | null>(null);
  const [votesPage, setVotesPage] = useState(1);

  const { data: idea, isLoading: isLoadingIdea, error: ideaError } = useGetIdeaQuery(ideaId ?? '', {
    skip: !ideaId,
  });
  const { data: votesResponse, isLoading: isLoadingVotes, isFetching: isFetchingVotes, error: votesError } =
    useGetIdeaVotesQuery(
      { ideaId: ideaId ?? '', page: votesPage, limit: VOTES_PAGE_SIZE },
      { skip: !ideaId }
    );
  const { data: organizations } = useGetOrganizationsSelectQuery();
  const { data: users } = useGetUsersQuery();
  const [updateIdea, { isLoading: isSaving }] = useUpdateIdeaMutation();
  const [deleteIdea, { isLoading: isDeleting }] = useDeleteIdeaMutation();

  const organizationNames = useMemo(
    () => new Map((organizations ?? []).map((organization) => [organization._id, organization.name])),
    [organizations]
  );

  const userNames = useMemo(
    () =>
      new Map(
        (users ?? []).map((user) => [
          user.id,
          formatPersonName(user.first_name, user.last_name, user.email),
        ])
      ),
    [users]
  );

  const ideaErrorMessage = ideaError ? getErrorMessage(ideaError) : null;
  const votesErrorMessage = votesError ? getErrorMessage(votesError) : null;

  const votesTotal = votesResponse?.total ?? 0;
  const currentVotesPage = votesResponse?.page ?? votesPage;
  const votesTotalPages = Math.max(1, Math.ceil(votesTotal / VOTES_PAGE_SIZE));
  const votesFrom = votesTotal === 0 ? 0 : (currentVotesPage - 1) * VOTES_PAGE_SIZE + 1;
  const votesTo = votesTotal === 0 ? 0 : Math.min(currentVotesPage * VOTES_PAGE_SIZE, votesTotal);

  const handleUpdate = async (payload: IdeaUpdateRequest) => {
    if (!idea) {
      return;
    }

    try {
      setEditorError(null);
      await updateIdea({ id: idea.id, body: payload }).unwrap();
      setIsEditOpen(false);
    } catch (requestError) {
      setEditorError(getErrorMessage(requestError));
    }
  };

  const handleDelete = async () => {
    if (!idea) {
      return;
    }

    if (!window.confirm(`Удалить идею «${idea.title}»?`)) {
      return;
    }

    try {
      setEditorError(null);
      await deleteIdea(idea.id).unwrap();
      navigate('/ideas');
    } catch (requestError) {
      setEditorError(getErrorMessage(requestError));
    }
  };

  if (!ideaId) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-red-700">
        Не передан идентификатор идеи.
      </div>
    );
  }

  if (isLoadingIdea) {
    return <div className="rounded-lg bg-white p-8 text-center text-gray-500 shadow-md">Загружаем идею...</div>;
  }

  if (!idea || ideaErrorMessage) {
    return (
      <div className="space-y-4">
        <button
          type="button"
          onClick={() => navigate('/ideas')}
          className="cursor-pointer text-sm font-medium text-blue-600 hover:text-blue-700"
        >
          ← К списку идей
        </button>
        <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-red-700">
          {ideaErrorMessage ?? 'Не удалось загрузить идею.'}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <button
          type="button"
          onClick={() => navigate('/ideas')}
          className="cursor-pointer text-sm font-medium text-blue-600 hover:text-blue-700"
        >
          ← К списку идей
        </button>
      </div>

      <section className="rounded-2xl bg-white p-8 shadow-md">
        <div className="flex flex-col gap-6 xl:flex-row xl:items-start xl:justify-between">
          <div className="min-w-0 flex-1">
            <div className="mb-4 flex flex-wrap gap-2">
              <span className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${STATUS_COLORS[idea.status_id]}`}>
                {STATUS_LABELS[idea.status_id]}
              </span>
              {idea.category_id ? (
                <span className="inline-flex rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
                  {CATEGORY_LABELS[idea.category_id]}
                </span>
              ) : null}
              <span className="inline-flex rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
                {ACCESSIBILITY_LABELS[idea.accessibility_id]}
              </span>
              {idea.dev_comment ? (
                <span className="inline-flex rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">
                  {idea.dev_comment}
                </span>
              ) : null}
            </div>

            <h1 className="text-3xl font-semibold text-gray-900">{idea.title}</h1>
            <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-gray-600">{idea.description}</p>

            <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
              <div className="rounded-xl bg-slate-50 p-4">
                <div className="text-xs uppercase tracking-wide text-gray-500">Организация</div>
                <div className="mt-2 text-sm font-medium text-gray-900">
                  {organizationNames.get(idea.organization_id) ?? idea.organization_id}
                </div>
              </div>
              <div className="rounded-xl bg-slate-50 p-4">
                <div className="text-xs uppercase tracking-wide text-gray-500">Автор</div>
                <div className="mt-2 text-sm font-medium text-gray-900">
                  {userNames.get(idea.user_id) ?? idea.user_id}
                </div>
              </div>
              <div className="rounded-xl bg-slate-50 p-4">
                <div className="text-xs uppercase tracking-wide text-gray-500">Создана</div>
                <div className="mt-2 text-sm font-medium text-gray-900">{formatDateTime(idea.created_at)}</div>
              </div>
              <div className="rounded-xl bg-slate-50 p-4">
                <div className="text-xs uppercase tracking-wide text-gray-500">Обновлена</div>
                <div className="mt-2 text-sm font-medium text-gray-900">{formatDateTime(idea.updated_at)}</div>
              </div>
            </div>
          </div>

          <div className="flex shrink-0 flex-col gap-3 xl:w-56">
            <button
              type="button"
              onClick={() => {
                setEditorError(null);
                setIsEditOpen(true);
              }}
              className="cursor-pointer rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              Редактировать
            </button>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-4">
          <div className="rounded-xl border border-gray-200 p-4">
            <div className="text-xs uppercase tracking-wide text-gray-500">Голоса организации</div>
            <div className="mt-2 text-2xl font-semibold text-gray-900">{idea.organization_votes_count ?? 0}</div>
          </div>
          <div className="rounded-xl border border-gray-200 p-4">
            <div className="text-xs uppercase tracking-wide text-gray-500">Всего голосов</div>
            <div className="mt-2 text-2xl font-semibold text-gray-900">{idea.votes_count ?? 0}</div>
          </div>
          <div className="rounded-xl border border-gray-200 p-4">
            <div className="text-xs uppercase tracking-wide text-gray-500">Затраты</div>
            <div className="mt-2 text-2xl font-semibold text-gray-900">{idea.costs ?? 0}</div>
          </div>
          <div className="rounded-xl border border-gray-200 p-4">
            <div className="text-xs uppercase tracking-wide text-gray-500">Критичность</div>
            <div className="mt-2 text-2xl font-semibold text-gray-900">{formatCriticality(idea.avg_criticality)}</div>
          </div>
        </div>

        {idea.note ? (
          <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4">
            <div className="text-sm font-medium text-amber-900">Заметка</div>
            <div className="mt-2 whitespace-pre-wrap text-sm text-amber-800">{idea.note}</div>
          </div>
        ) : null}
      </section>

      <section className="rounded-2xl bg-white p-8 shadow-md">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Голоса пользователей</h2>
            <p className="mt-1 text-sm text-gray-500">
              Ниже показаны все голоса по этой идее. Открытие отдельного голоса не добавлял.
            </p>
          </div>
          <div className="text-sm text-gray-500">
            {isFetchingVotes ? 'Обновляем...' : `Показано ${votesFrom}-${votesTo} из ${votesTotal}`}
          </div>
        </div>

        {votesErrorMessage ? (
          <div className="mb-6 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {votesErrorMessage}
          </div>
        ) : null}

        {isLoadingVotes ? (
          <div className="py-10 text-center text-gray-500">Загружаем голоса...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-[1100px] table-fixed divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="w-[160px] px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Дата</th>
                  <th className="w-[220px] px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Организация</th>
                  <th className="w-[220px] px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Пользователь</th>
                  <th className="w-[140px] px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Критичность</th>
                  <th className="w-[360px] px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Комментарий</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {votesResponse?.items.length ? (
                  votesResponse.items.map((vote) => (
                    <tr key={vote.id}>
                      <td className="px-6 py-4 align-top text-sm text-gray-700">{formatDateTime(vote.created_at)}</td>
                      <td className="px-6 py-4 align-top text-sm text-gray-700">
                        {organizationNames.get(vote.organization_id) ?? vote.organization_id}
                      </td>
                      <td className="px-6 py-4 align-top text-sm text-gray-700">
                        {userNames.get(vote.user_id) ?? vote.user_id}
                      </td>
                      <td className="px-6 py-4 align-top text-sm text-gray-700">
                        {formatVoteCriticality(vote.answer?.importance)}
                      </td>
                      <td className="px-6 py-4 align-top text-sm text-gray-700">
                        {vote.description || '—'}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="px-6 py-10 text-center text-sm text-gray-500">
                      По этой идее пока нет голосов.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-6 flex items-center justify-between">
          <div className="text-sm text-gray-500">
            {votesTotal === 0 ? 'Нет голосов' : `Показано ${votesFrom}-${votesTo} из ${votesTotal} голосов`}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setVotesPage((prev) => Math.max(1, prev - 1))}
              className="cursor-pointer rounded-md bg-gray-600 px-4 py-2 text-sm font-medium text-white hover:bg-gray-700 disabled:bg-gray-300"
              disabled={currentVotesPage <= 1 || isFetchingVotes}
            >
              Назад
            </button>
            <button
              type="button"
              onClick={() => setVotesPage((prev) => Math.min(votesTotalPages, prev + 1))}
              className="cursor-pointer rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:bg-blue-300"
              disabled={currentVotesPage >= votesTotalPages || isFetchingVotes}
            >
              Вперед
            </button>
          </div>
        </div>
      </section>

      <Modal
        isOpen={isEditOpen}
        onClose={() => {
          setEditorError(null);
          setIsEditOpen(false);
        }}
        title={`Редактирование идеи: ${idea.title}`}
      >
        <IdeaEditorForm
          idea={idea}
          organizationName={organizationNames.get(idea.organization_id) ?? idea.organization_id}
          authorName={userNames.get(idea.user_id) ?? idea.user_id}
          onCancel={() => {
            setEditorError(null);
            setIsEditOpen(false);
          }}
          onDelete={handleDelete}
          onSubmit={handleUpdate}
          isSaving={isSaving}
          isDeleting={isDeleting}
          errorMessage={editorError}
        />
      </Modal>
    </div>
  );
};
